import { AppArtBucket } from '../types';
import { rasterizePdf } from './rasterizePdf';

export interface InspectedFile {
  file: File;
  /** PNG rasterizado quando o formato não renderiza no browser (PDF/AI). */
  previewBlob: Blob | null;
  /** URL local para o card enquanto não subiu. */
  objectUrl: string | null;
  width: number | null;
  height: number | null;
  hasAlpha: boolean | null;
  pageCount: number | null;
  warnings: string[];
}

const PDF_LIKE = ['application/pdf', 'application/postscript', 'application/illustrator'];
const PDF_EXT = ['pdf', 'ai', 'eps'];

export const getExtension = (name: string) =>
  (name.split('.').pop() || '').toLowerCase();

const isPdfLike = (file: File) =>
  PDF_LIKE.includes(file.type) || PDF_EXT.includes(getExtension(file.name));

const isHeic = (file: File) =>
  ['image/heic', 'image/heif'].includes(file.type) ||
  ['heic', 'heif'].includes(getExtension(file.name));

/**
 * `file-type` do backend não reconhece SVG (é texto, sem magic bytes) e devolve
 * `application/xml` — o S3 então serve a logo como XML e o browser mostra a
 * árvore de tags. Por isso o mime vai explícito daqui, do `File.type`.
 */
export const resolveMimeType = (file: File) => {
  if (file.type) { return file.type; }
  const ext = getExtension(file.name);
  if (ext === 'svg') { return 'image/svg+xml'; }
  if (ext === 'pdf') { return 'application/pdf'; }
  return 'application/octet-stream';
};

const readImage = (url: string) => new Promise<HTMLImageElement | null>(resolve => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = () => resolve(null);
  img.src = url;
});

/**
 * Varre o canal alpha por amostragem. Não precisa ser exato — serve para dizer
 * "essa logo tem fundo branco" antes de ela virar ícone.
 */
const detectAlpha = (img: HTMLImageElement): boolean | null => {
  const canvas = document.createElement('canvas');
  const edge = Math.min(160, Math.max(img.naturalWidth, img.naturalHeight));
  canvas.width = edge;
  canvas.height = edge;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) { return null; }

  context.drawImage(img, 0, 0, edge, edge);
  try {
    const { data } = context.getImageData(0, 0, edge, edge);
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] < 250) { return true; }
    }
    return false;
  } catch {
    return null; // canvas "sujo" (SVG externo) — não dá para ler
  }
};

const buildWarnings = (bucket: AppArtBucket, file: File, info: {
  width: number | null; height: number | null; hasAlpha: boolean | null;
  pageCount: number | null; isVector: boolean;
}): string[] => {
  const warnings: string[] = [];
  const { width, height, hasAlpha, pageCount, isVector } = info;
  const longestEdge = Math.max(width || 0, height || 0);

  if (isHeic(file)) {
    warnings.push('Formato HEIC não abre no navegador — peça para reenviar como JPG.');
  }
  if (file.size > 8 * 1024 * 1024) {
    warnings.push('Arquivo acima de 8 MB — pode falhar no envio.');
  }

  if (bucket === AppArtBucket.logo) {
    if (!isVector && longestEdge && longestEdge < 512) {
      warnings.push(`Logo pequena (${longestEdge}px) — pode sair borrada no ícone.`);
    }
    if (!isVector && hasAlpha === false) {
      warnings.push('Logo sem fundo transparente — peça PDF ou PNG com transparência.');
    }
    if (pageCount && pageCount > 1) {
      warnings.push(`PDF com ${pageCount} páginas — parece manual de marca; confirme se a logo está na página 1.`);
    }
  }

  if (bucket === AppArtBucket.stationPhoto && longestEdge && longestEdge < 1000) {
    warnings.push(`Imagem de baixa resolução (${width}×${height}) — pode não servir para screenshot.`);
  }

  return warnings;
};

/**
 * Lê o que dá do arquivo no browser antes de subir. Nada aqui bloqueia: este é
 * material bruto do cliente, não asset final — a spec de produção só vale no
 * export da pasta wl-src/.
 */
export const inspectFile = async (file: File, bucket: AppArtBucket): Promise<InspectedFile> => {
  let previewBlob: Blob | null = null;
  let width: number | null = null;
  let height: number | null = null;
  let hasAlpha: boolean | null = null;
  let pageCount: number | null = null;

  const isVector = isPdfLike(file) || getExtension(file.name) === 'svg';

  if (isPdfLike(file)) {
    const raster = await rasterizePdf(file);
    if (raster) {
      previewBlob = raster.blob;
      width = raster.width;
      height = raster.height;
      pageCount = raster.pageCount;
      hasAlpha = true; // canvas do pdf.js sai com fundo transparente
    }
  } else if (file.type.startsWith('image/')) {
    const url = URL.createObjectURL(file);
    const img = await readImage(url);
    if (img) {
      width = img.naturalWidth || null;
      height = img.naturalHeight || null;
      hasAlpha = detectAlpha(img);
    }
    URL.revokeObjectURL(url);
  }

  const objectUrl = previewBlob
    ? URL.createObjectURL(previewBlob)
    : (file.type.startsWith('image/') ? URL.createObjectURL(file) : null);

  return {
    file,
    previewBlob,
    objectUrl,
    width,
    height,
    hasAlpha,
    pageCount,
    warnings: buildWarnings(bucket, file, { width, height, hasAlpha, pageCount, isVector }),
  };
};
